import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useDemo } from '@/contexts/DemoContext';
import { Car } from 'lucide-react';
import API from '@/utils/api';
import GlobalHeader from '@/components/GlobalHeader';
import ParkingTabs from '@/components/parking/ParkingTabs';
import { toast } from 'sonner';
import APP_ICONS from '@/utils/appIcons';

import ActiveParkingsTab from '@/components/parking/ActiveParkingsTab';
import HistoryTab from '@/components/parking/HistoryTab';
import PlacesTab from '@/components/parking/PlacesTab';

// Parking module lavender theme tokens
const PARKING_THEME = {
  primary: '#C4B5FD',
  light: '#EDE9FE',
  lighter: '#F5F3FF',
  dark: '#5B21B6',
  icon: '#8B5CF6',
  surface: '#FAFAFD',
};

const ParkingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isDemoMode, getVehicles } = useDemo();
  const [vehicles, setVehicles] = useState([]);
  const [activeTab, setActiveTab] = useState('active');
  const [refreshKey, setRefreshKey] = useState(0);

  const fetchVehicles = useCallback(async () => {
    // In demo mode, use demo data instead of API
    if (isDemoMode) {
      setVehicles(getVehicles());
      return;
    }
    
    try {
      const response = await API.get('/vehicles');
      setVehicles(response.data);
    } catch (error) {
      console.error('Failed to fetch vehicles:', error);
    }
  }, [isDemoMode, getVehicles]);

  useEffect(() => {
    fetchVehicles();
  }, [fetchVehicles]);

  const handleRefresh = useCallback(async () => {
    await fetchVehicles();
    setRefreshKey(prev => prev + 1);
    toast.success('Data refreshed');
  }, [fetchVehicles]);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'active':
        return <ActiveParkingsTab key={`active-${refreshKey}`} vehicles={vehicles} />;
      case 'history':
        return <HistoryTab key={`history-${refreshKey}`} vehicles={vehicles} />;
      case 'places':
        return <PlacesTab key={`places-${refreshKey}`} />;
      default:
        return <ActiveParkingsTab key={`active-${refreshKey}`} vehicles={vehicles} />;
    }
  };

  return (
    <div 
      className="min-h-screen"
      style={{ backgroundColor: PARKING_THEME.surface }}
    >
      <GlobalHeader
        iconSrc={APP_ICONS.parking}
        iconBgColor={PARKING_THEME.light}
        iconColor="text-purple-700"
        title="Parking"
        subtitle="Find your car easily"
        onRefresh={handleRefresh}
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-28">
        <ParkingTabs
          activeTab={activeTab}
          onTabChange={setActiveTab}
        >
          {renderTabContent()}
        </ParkingTabs>
      </main>
    </div>
  );
};

export default ParkingPage;
